import type { GraphData, GraphNode, GraphEdge, DataGeneratorConfig } from '../types/graph'

// 预定义的数据模板
const dataTemplates = {
  social: {
    nodeTypes: ['Person', 'Organization', 'Location', 'Event'],
    edgeTypes: ['knows', 'works_at', 'lives_in', 'attended', 'friends_with'],
    colors: [
      '#4F46E5', '#059669', '#DC2626', '#D97706', '#7C3AED',
      '#0891B2', '#EC4899', '#F59E0B', '#10B981', '#8B5CF6',
      '#EF4444', '#06B6D4', '#84CC16', '#F97316', '#6366F1'
    ],
    typeColors: {
      Person: '#4F46E5',
      Organization: '#059669',
      Location: '#DC2626',
      Event: '#D97706'
    },
    names: {
      Person: ['张三', '李四', '王五', '赵六', '钱七', '孙八', '周九', '吴十', '郑十一', '王十二'],
      Organization: ['阿里巴巴', '腾讯', '百度', '字节跳动', '美团', '滴滴', '小米', '华为', '京东', '网易'],
      Location: ['北京', '上海', '深圳', '杭州', '广州', '成都', '武汉', '西安', '南京', '苏州'],
      Event: ['技术大会', '产品发布会', '年会', '培训', '会议', '展览', '论坛', '研讨会', '峰会', '沙龙']
    }
  },
  academic: {
    nodeTypes: ['Paper', 'Author', 'Institution', 'Conference', 'Journal'],
    edgeTypes: ['authored_by', 'published_in', 'affiliated_with', 'cites', 'collaborates_with'],
    colors: [
      '#7C3AED', '#0891B2', '#059669', '#DC2626', '#D97706',
      '#4F46E5', '#EC4899', '#F59E0B', '#10B981', '#8B5CF6',
      '#EF4444', '#06B6D4', '#84CC16', '#F97316', '#6366F1'
    ],
    typeColors: {
      Paper: '#7C3AED',
      Author: '#0891B2',
      Institution: '#059669',
      Conference: '#DC2626',
      Journal: '#D97706'
    },
    names: {
      Paper: ['深度学习研究', '机器学习算法', '自然语言处理', '计算机视觉', '数据挖掘', '人工智能', '神经网络', '强化学习', '知识图谱', '推荐系统'],
      Author: ['张教授', '李博士', '王研究员', '赵院士', '钱学者', '孙专家', '周老师', '吴导师', '郑研究生', '王博士后'],
      Institution: ['清华大学', '北京大学', '中科院', '复旦大学', '上海交大', '浙江大学', '南京大学', '华中科大', '西安交大', '哈工大'],
      Conference: ['AAAI', 'IJCAI', 'ICML', 'NeurIPS', 'ICLR', 'ACL', 'EMNLP', 'CVPR', 'ICCV', 'ECCV'],
      Journal: ['Nature', 'Science', 'Cell', 'PNAS', 'IEEE Trans', 'ACM Trans', 'JMLR', 'AI Journal', 'Pattern Recognition', 'Neural Networks']
    }
  },
  business: {
    nodeTypes: ['Company', 'Product', 'Market', 'Technology', 'Customer'],
    edgeTypes: ['produces', 'competes_in', 'uses', 'serves', 'partners_with'],
    colors: [
      '#1F2937', '#059669', '#DC2626', '#7C3AED', '#D97706',
      '#4F46E5', '#0891B2', '#EC4899', '#F59E0B', '#10B981',
      '#8B5CF6', '#EF4444', '#06B6D4', '#84CC16', '#F97316'
    ],
    typeColors: {
      Company: '#1F2937',
      Product: '#059669',
      Market: '#DC2626',
      Technology: '#7C3AED',
      Customer: '#D97706'
    },
    names: {
      Company: ['苹果', '微软', '谷歌', '亚马逊', '特斯拉', 'Meta', 'Netflix', 'Adobe', 'Salesforce', 'Oracle'],
      Product: ['iPhone', 'Windows', 'Android', 'AWS', 'Model S', 'Instagram', 'Photoshop', 'Office 365', 'Gmail', 'YouTube'],
      Market: ['智能手机', '云计算', '电动汽车', '社交媒体', '流媒体', '企业软件', '搜索引擎', '电商', '游戏', '广告'],
      Technology: ['AI', '5G', '区块链', '物联网', 'VR/AR', '量子计算', '边缘计算', '自动驾驶', '机器人', '生物技术'],
      Customer: ['企业客户', '个人用户', '开发者', '学生', '政府', '医院', '学校', '银行', '零售商', '制造商']
    }
  },
  biological: {
    nodeTypes: ['Gene', 'Protein', 'Disease', 'Drug', 'Pathway'],
    edgeTypes: ['encodes', 'interacts_with', 'causes', 'treats', 'regulates'],
    colors: [
      '#059669', '#0891B2', '#DC2626', '#7C3AED', '#D97706',
      '#4F46E5', '#EC4899', '#F59E0B', '#10B981', '#8B5CF6',
      '#EF4444', '#06B6D4', '#84CC16', '#F97316', '#6366F1'
    ],
    typeColors: {
      Gene: '#059669',
      Protein: '#0891B2',
      Disease: '#DC2626',
      Drug: '#7C3AED',
      Pathway: '#D97706'
    },
    names: {
      Gene: ['BRCA1', 'TP53', 'EGFR', 'KRAS', 'PIK3CA', 'APC', 'PTEN', 'RB1', 'VHL', 'MLH1'],
      Protein: ['p53', 'EGFR', 'mTOR', 'NF-κB', 'STAT3', 'c-Myc', 'β-catenin', 'VEGF', 'TNF-α', 'IL-6'],
      Disease: ['癌症', '糖尿病', '高血压', '阿尔茨海默病', '帕金森病', '心脏病', '中风', '肾病', '肝病', '肺病'],
      Drug: ['阿司匹林', '胰岛素', '青霉素', '他汀类', 'ACE抑制剂', '质子泵抑制剂', '抗抑郁药', '抗癌药', '免疫抑制剂', '抗病毒药'],
      Pathway: ['细胞凋亡', '细胞周期', 'DNA修复', '免疫反应', '代谢途径', '信号转导', '转录调控', '蛋白质合成', '细胞分化', '炎症反应']
    }
  },
  technology: {
    nodeTypes: ['Framework', 'Language', 'Database', 'Tool', 'Platform'],
    edgeTypes: ['built_with', 'integrates_with', 'depends_on', 'supports', 'replaces'],
    colors: [
      '#4F46E5', '#059669', '#DC2626', '#D97706', '#7C3AED',
      '#0891B2', '#EC4899', '#F59E0B', '#10B981', '#8B5CF6',
      '#EF4444', '#06B6D4', '#84CC16', '#F97316', '#6366F1'
    ],
    typeColors: {
      Framework: '#4F46E5',
      Language: '#059669',
      Database: '#DC2626',
      Tool: '#D97706',
      Platform: '#7C3AED'
    },
    names: {
      Framework: ['React', 'Vue', 'Angular', 'Spring', 'Django', 'Express', 'Laravel', 'Rails', 'Flutter', 'React Native'],
      Language: ['JavaScript', 'Python', 'Java', 'TypeScript', 'Go', 'Rust', 'C++', 'C#', 'PHP', 'Swift'],
      Database: ['MySQL', 'PostgreSQL', 'MongoDB', 'Redis', 'Elasticsearch', 'Cassandra', 'Neo4j', 'InfluxDB', 'SQLite', 'Oracle'],
      Tool: ['Docker', 'Kubernetes', 'Git', 'Jenkins', 'Webpack', 'Babel', 'ESLint', 'Jest', 'Cypress', 'Postman'],
      Platform: ['AWS', 'Azure', 'GCP', 'Vercel', 'Netlify', 'Heroku', 'DigitalOcean', 'GitHub', 'GitLab', 'Bitbucket']
    }
  }
}

export class DataGenerator {
  private config: DataGeneratorConfig
  private template: any

  constructor(config: DataGeneratorConfig) {
    this.config = config
    this.template = dataTemplates[config.type]
    if (config.seed) {
      this.setSeed(config.seed)
    }
  }

  private customRandom: () => number = Math.random;

  private setSeed(seed: number) {
    // 简单的伪随机数生成器
    let m = 0x80000000 // 2**31
    let a = 1103515245
    let c = 12345
    let state = seed
    this.customRandom = function() {
      state = (a * state + c) % m
      return state / (m - 1)
    }
  }

  private getRandomItem<T>(array: T[]): T {
    return array[Math.floor(this.customRandom() * array.length)]
  }

  private generateNodes(): GraphNode[] {
    const nodes: GraphNode[] = []
    const { nodeTypes, colors, typeColors, names } = this.template

    for (let i = 0; i < this.config.nodeCount; i++) {
      const type = this.getRandomItem(nodeTypes) as string
      const nameList = names[type]
      const name = this.getRandomItem(nameList)

      // 使用更丰富的颜色配置，优先使用类型颜色，然后随机选择
      let nodeColor = typeColors[type]
      if (!nodeColor || this.customRandom() > 0.7) {
        nodeColor = this.getRandomItem(colors)
      }

      nodes.push({
        id: `node_${i}`,
        label: `${name}_${i}`,
        type,
        color: nodeColor,
        size: 10,//Math.random() * 20 + 10,
        shape: 'dot',
        properties: {
          description: `这是一个${type}类型的节点`,
          createdAt: new Date().toISOString()
        }
      })
    }

    return nodes
  }

  private generateEdges(nodes: GraphNode[]): GraphEdge[] {
    const edges: GraphEdge[] = []
    const { edgeTypes } = this.template
    const edgeCount = Math.min(this.config.edgeCount, nodes.length * (nodes.length - 1) / 2)

    const usedPairs = new Set<string>()

    for (let i = 0; i < edgeCount; i++) {
      let fromNode, toNode, pairKey
      let attempts = 0

      do {
        fromNode = this.getRandomItem(nodes)
        toNode = this.getRandomItem(nodes)
        pairKey = `${fromNode.id}-${toNode.id}`
        attempts++
      } while ((fromNode.id === toNode.id || usedPairs.has(pairKey)) && attempts < 100)

      if (attempts >= 100) break

      usedPairs.add(pairKey)

      const edgeType = this.getRandomItem(edgeTypes) as string

      // 边的颜色跟随源节点的颜色，稍微调暗一些
      const edgeColor = this.adjustColorOpacity(fromNode.color || '#666666', 0.8)

      edges.push({
        id: `edge_${i}`,
        from: fromNode.id,
        to: toNode.id,
        label: edgeType,
        type: edgeType,
        color: edgeColor,
        width: 0.5,//Math.random() * 3 + 1,
        arrows: 'to',
        properties: {
          weight: this.customRandom(),
          createdAt: new Date().toISOString()
        }
      })
    }

    return edges
  }

  // 调整颜色透明度的辅助方法
  private adjustColorOpacity(color: string, opacity: number): string {
    // 如果是十六进制颜色，转换为 rgba
    if (color.startsWith('#')) {
      const hex = color.slice(1)
      const r = parseInt(hex.slice(0, 2), 16)
      const g = parseInt(hex.slice(2, 4), 16)
      const b = parseInt(hex.slice(4, 6), 16)
      return `rgba(${r}, ${g}, ${b}, ${opacity})`
    }
    return color
  }

  public generate(): GraphData {
    const nodes = this.generateNodes()
    const edges = this.generateEdges(nodes)
    
    return {
      nodes,
      edges
    }
  }
}

export function generateGraphData(config: DataGeneratorConfig): GraphData {
  const generator = new DataGenerator(config)
  return generator.generate()
}